import React, { useState, useRef, useEffect } from 'react'
import { Link, graphql } from 'gatsby'
import tw from 'twin.macro'

import Layout from '../components/Layout'
import {
  ButtonStyle,
  CopyLGStyle,
  CopyStyle,
  HeaderLGStyle,
  HeaderStyle,
  HeaderXLStyle,
  LinkButton,
  SubheadSMStyle,
  SubheadStyle,
} from '../components/UI'

import Clients from '../components/Clients'
import Partners from '../components/Partners'
import Testimonials from '../components/Testimonials'
import FrequentlyAskedQuestions from '../components/FrequentlyAskedQuestions'
import CallToAction from '../components/CallToAction'
import { GatsbyImage } from 'gatsby-plugin-image'
import Seo from '../components/Seo'
import { useForm } from 'react-hook-form'
import appStore from '../images/app-store.svg'
import googlePlay from '../images/google-play.svg'
import { setUtmParams, areUtmParamsAvailable } from '../utils';
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import HubSpotHealthcareForm from '../components/HubSpotHealthcareForm'

const FindTalent = ({ location, data }) => {
  const pageData = data.contentfulFindTalentPage
  const globalData = data.contentfulGlobalSettings

  const findTalentForm = useRef(null);

  const dispatch = useDispatch();
  const utmParams = useSelector((state) => state.utmParams);

  useEffect(() => {
    if (!areUtmParamsAvailable(utmParams)) {
      dispatch({ type: "SAVE_UTM_PARAMS", payload: setUtmParams() });
    }
  }, [dispatch])

  const scrollToFindTalentForm = () => {
    findTalentForm.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout location={location}>
      <Seo
        title={pageData.metadataTitle || 'Find Talent'}
        description={pageData.metadataDescription}
        image={
          pageData.metadataImage
            ? `https:${pageData.metadataImage.file.url}`
            : undefined
        }
      />
      {/* Hero */}
      <section>
        <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative h-[575px])">
          {/* Image */}
          <div tw="order-1 lg:(order-2 col-start-7 col-span-8 relative)">
            <GatsbyImage
              tw="w-full lg:(absolute w-full h-full object-cover)"
              image={pageData.heroImage.gatsbyImageData}
              alt={pageData.heroImage.filename}
            />
            <div tw="lg:(absolute w-36 h-full bg-gradient-to-r from-white to-transparent top-0 left-[-1px])" />
          </div>
          {/* Content */}
          <div tw="order-2 mx-7 mb-9 lg:(mx-0 mb-0 order-1 col-start-2 col-span-5) flex flex-col justify-center">
            <h2 css={[HeaderLGStyle, tw`mt-8 mb-6 hidden lg:block`]}>
              {pageData.heroHeadline}
            </h2>
            <h2 css={[HeaderXLStyle, tw`mt-8 mb-6 block lg:hidden`]}>
              {pageData.heroHeadline}
            </h2>
            {/* <div css={[CopyStyle, tw`flex font-medium mb-[22px]`]}>
              <div tw="px-[12px] py-[6px] border border-[#FF4D00] mr-[28px] ">
                Healthcare
              </div>
              <a
                href="/hospitality"
                tw="bg-[rgba(242, 252, 225, 0.3)] border border-[#C2E1EA] px-[12px] py-[6px]"
              >
                <span tw="opacity-30">Hospitality</span>
              </a>
            </div> */}
            <p css={[CopyLGStyle, tw`mb-12`]}>
              {pageData.heroSubhead.heroSubhead}
            </p>
            <div>
              <div tw='block lg:(inline-block)'>
                <button
                  css={[
                    ButtonStyle,
                    tw`inline-block py-4 px-8 w-full text-center border-[#FF4D00] border-[2px] lg:(px-8 w-auto)`,
                    tw`transition-all transition-duration[0.2s] transition-timing-function[cubic-bezier(0.645,0.045,0.355,1)] hover:(text-white bg-[#FF4D00])`,
                  ]}
                  onClick={scrollToFindTalentForm}
                >
                  <span tw="px-4">{pageData.heroCta}</span>
                </button>
              </div>
              <div tw='block text-center mt-4 lg:(inline-block mt-0)'>
                <a href="/hospitality/find-talent" tw="text-[#FF4D00] underline lg:(ml-4)">
                  I'm a hospitality company
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="book-a-demo" tw="mx-7 pt-[150px] pb-[150px] lg:(max-w-screen-xl mx-auto)">
        <div
          tw="grid grid-cols-4 lg:(grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative)"
        >
          <div tw="col-span-4 lg:(col-start-2 col-span-5 pr-10) flex items-center">
            <div css={[tw`block`]}>
              <h2 css={[HeaderStyle, tw`mb-[24px]`]}>
                How can Staffy&nbsp;help&nbsp;you?
              </h2>
              <p css={[CopyLGStyle, tw`mb-[30px]`]}>
                Fill out this form and a member of our team will reach out
                with more information to help you get started with Staffy.
              </p>
            </div>
          </div>
          <div css={[tw`col-span-4 lg:(col-start-8 col-span-7)`]}>
            <HubSpotHealthcareForm />
          </div>
        </div>
      </section>

      {/* Features */}
      {pageData.section1Features && (
        <section tw="max-w-screen-xl mx-auto">
          <div tw="my-10 lg:my-20">
            <h2 css={[HeaderStyle, tw`mx-auto text-center mb-10 lg:(mb-16)`]}>
              {pageData.section1Headline}
            </h2>
            <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr])">
              {/* Image */}
              <GatsbyImage
                tw="w-full mb-11 lg:(col-span-6 mb-0 object-cover)"
                image={pageData.section1Image.gatsbyImageData}
                alt={pageData.section1Image.filename}
              />
              {/* List */}
              <div tw="mx-7 lg:(mx-0 mb-0 col-start-8 col-span-6 relative flex flex-col justify-center)">
                <ul tw="lg:(flex flex-wrap justify-between)">
                  {pageData.section1Features.map((feature, index) => (
                    <li
                      tw="w-full mb-12 text-center lg:(text-left w-1/2 pr-10)"
                      key={`staffy-feature-${index}`}
                    >
                      <GatsbyImage
                        objectFit="contain"
                        tw="w-[40px] mb-2"
                        image={feature.icon.gatsbyImageData}
                        alt={feature.icon.title}
                      />
                      <h3
                        css={[
                          SubheadStyle,
                          tw`text-center text-[#FF4D00] mb-4 lg:(text-left)`,
                        ]}
                      >
                        {feature.headline}
                      </h3>
                      <p css={[CopyStyle, tw`text-center lg:(text-left)`]}>
                        {feature.description.description}
                      </p>
                    </li>
                  ))}
                </ul>
                <div>
                  <LinkButton
                    destination="/about"
                    tw="inline-block py-3 w-full text-center border-[#FF4D00] border-[1.5px] lg:(w-auto px-8)"
                  >
                    {pageData.section1Cta}
                  </LinkButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Clients */}
      {pageData.clients && <Clients clients={pageData.clients} />}

      {/* Partners */}
      {pageData.partners && <Partners partners={pageData.partners} />}

      {/* Testimonials */}
      {pageData.testimonialsTestimonials && (
        <section tw="py-7 lg:(pt-16) overflow-hidden">
          <div tw="mx-7 lg:(max-w-screen-lg mx-auto relative)">
            <h2 css={[SubheadStyle, tw`mb-9`]}>
              {pageData.testimonialsHeadline}
            </h2>
            <Testimonials
              testimonials={pageData.testimonialsTestimonials}
              onWhite
            />
          </div>
        </section>
      )}

      {/* FAQ */}
      {pageData.faqQuestions && (
        <FrequentlyAskedQuestions
          headline={pageData.faqHeadline}
          subhead={pageData.faqSubhead.faqSubhead}
          questions={pageData.faqQuestions}
          image={pageData.faqImage}
        />
      )}

      {/* Form */}
      {/* <section tw="mx-7 pt-[100px] pb-[50px] lg:(max-w-screen-xl mx-auto)" ref={findTalentForm}>
        <div
          tw="grid grid-cols-4 lg:(grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative)"
        >
          <div tw="col-span-4 lg:(col-start-2 col-span-5 pr-10) flex items-center">
            <div css={[tw`block`]}>
              <h2 css={[HeaderStyle, tw`mb-[24px]`]}>
                How can Staffy&nbsp;help&nbsp;you?
              </h2>
              <p css={[CopyLGStyle, tw`mb-[30px]`]}>
                Fill out this form and a member of our team will reach out
                with more information to help you get started with Staffy.
              </p>
            </div>
          </div>
          <div css={[tw`col-span-4 lg:(col-start-8 col-span-7)`]}>
            <div id="HSHealthcareFormBottom" />
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <CallToAction
        header={pageData.ctaHeadline}
        copy={pageData.ctaSubhead.ctaSubhead}
        ctas={[
          { copy: 'Start Earning', destination: '/start-earning' },
          { copy: 'Find Talent', destination: '/find-talent' },
        ]}
        image={pageData.ctaImage}
      />
    </Layout>
  )
}

export default FindTalent

export const pageQuery = graphql`
  {
    contentfulFindTalentPage(pageId: { eq: "Find Talent" }) {
      heroHeadline
      heroSubhead {
        heroSubhead
      }
      heroCta
      heroImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: DOMINANT_COLOR
          width: 1024
          quality: 80
        )
      }
      section1Headline
      section1Image {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: DOMINANT_COLOR
          width: 1024
          quality: 80
        )
      }
      section1Features {
        icon {
          title
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: DOMINANT_COLOR
            width: 36
            quality: 100
          )
        }
        headline
        description {
          description
        }
      }
      section1Cta
      clients {
        name
        logo {
          filename
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: DOMINANT_COLOR
            width: 300
            quality: 100
          )
        }
      }
      partners {
        name
        logo {
          filename
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: DOMINANT_COLOR
            width: 300
            quality: 100
          )
        }
      }
      testimonialsHeadline
      testimonialsTestimonials {
        copy {
          copy
        }
        name
        initials
        role
      }
      faqImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: DOMINANT_COLOR
          width: 1024
          quality: 80
        )
      }
      faqHeadline
      faqSubhead {
        faqSubhead
      }
      faqQuestions {
        question
        answer {
          raw
        }
      }
      ctaHeadline
      ctaSubhead {
        ctaSubhead
      }
      ctaImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: DOMINANT_COLOR
          width: 1024
          quality: 80
        )
      }
      metadataTitle
      metadataDescription
      metadataImage {
        file {
          url
        }
      }
    }

    contentfulGlobalSettings(pageId: { eq: "Globals" }) {
      iosDownloadLink
      androidDownloadLink
    }
  }
`
